import { fetchConfig, getPromptsConfig } from '@app/config-builder/common/utils';
import { ConfigType } from '@app/config-builder/store';
import { merge } from 'lodash';
import { reOpenChat } from '@/modules/api';
import { defaultTranslations } from '@/modules/common';
import { isWidgetReadyAtom, store } from '@/store';
import {
  cleanupModule,
  reopenChat as dispatchReopenChat,
  getSuggestion,
  getUserTestGroup,
  initExternalModule,
  loadDevConfig,
  resetChat,
  resetConfig,
  sendNewMessage,
  showCrossSale,
  toggleAppChat,
  toggleCart,
  toggleTooltip,
  triggerSemiOpen,
  updateAppParams,
  updateProductList,
  updateSession,
} from '@/utils';

const PAGE_TYPES = {
  PRODUCT: 'PRODUCT',
  COLLECTION: 'COLLECTION',
  DEFAULT: 'DEFAULT',
  HOME: 'HOME',
  CART: 'CART',
};

function isMobile() {
  const isUserAgentMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop|BlackBerry/i.test(
    navigator.userAgent
  );
  const isSmallScreen = window.matchMedia('(max-width: 767px)').matches;
  return isUserAgentMobile || isSmallScreen;
}

function getCountryCode() {
  const cookie = document.cookie.match(/localization=([^;]+)/);
  if (cookie) {
    return cookie[1];
  }
  return '';
}

function getLocale() {
  return window.Shopify?.locale ?? 'en';
}

function detectPageType(isCartOpen?: () => boolean) {
  const pathname = window.location.pathname;
  if (isCartOpen?.() || pathname.startsWith('/cart')) {
    return PAGE_TYPES.CART;
  }
  if (pathname.includes('/products')) {
    return PAGE_TYPES.PRODUCT;
  }
  if (pathname.includes('/collections')) {
    return PAGE_TYPES.COLLECTION;
  }
  if (pathname === '/') {
    return PAGE_TYPES.HOME;
  }
  return PAGE_TYPES.DEFAULT;
}

const reopenChat = async (params: { apiKey: string; token: string; chatId: number }) => {
  const { id_token, chat_id } = await reOpenChat(params);
  dispatchReopenChat({ token: id_token, chatId: chat_id ?? params.chatId });
  toggleAppChat(true);
};

const onReady = (callback: () => void) => {
  if (store.get(isWidgetReadyAtom)) {
    callback();
    return () => {};
  }
  const unsubscribe = store.sub(isWidgetReadyAtom, () => {
    if (store.get(isWidgetReadyAtom)) {
      unsubscribe();
      callback();
    }
  });
  return unsubscribe;
};

type WaitForStablePositionOptions = {
  axis?: 'left' | 'top';
  maxFrames?: number;
  stableFramesNeeded?: number;
};

const waitForStablePosition = (el: HTMLElement, callback: () => void, options: WaitForStablePositionOptions = {}) => {
  if (!el || !callback || typeof callback !== 'function') {
    return;
  }
  const { axis = 'left', maxFrames = 45, stableFramesNeeded = 3 } = options;
  let lastValue: number | null = null;
  let stableFrames = 0;
  let frame = 0;

  const check = () => {
    const value = el.getBoundingClientRect()[axis];
    stableFrames = value === lastValue ? stableFrames + 1 : 0;
    lastValue = value;
    frame += 1;
    if (stableFrames >= stableFramesNeeded || frame >= maxFrames) {
      callback();
      return;
    }
    requestAnimationFrame(check);
  };

  // starts check
  requestAnimationFrame(check);
};

const sayWhattChat = {
  PAGE_TYPES,
  isMobile,
  getCountryCode,
  getLocale,
  detectPageType,
  getUserTestGroup,
  init: initExternalModule('app'),
  toggleChat: toggleAppChat,
  updateParams: updateAppParams,
  toggleCart: toggleCart,
  updateSession: updateSession,
  sendNewMessage: sendNewMessage,
  resetConfig: resetConfig,
  toggleTooltip: toggleTooltip,
  updateProductList: updateProductList,
  getSuggestion: getSuggestion,
  resetChat: resetChat,
  reopenChat: reopenChat,
  onReady: onReady,
  showCrossSale: showCrossSale,
  triggerSemiOpen: triggerSemiOpen,
  destroy: cleanupModule,
  fetchConfig: fetchConfig,
  getPromptsConfig: getPromptsConfig,
  waitForStablePosition: waitForStablePosition,
  mergeTheme: (baseTheme: ConfigType['theme'], overrides: Partial<ConfigType['theme']>) =>
    merge({}, baseTheme, overrides),
  defaultTranslations,
};

window.sayWhattChat = sayWhattChat;

if (import.meta.env.DEV && window.configUrl) {
  const scriptId = 'devConfig';
  loadDevConfig(scriptId, window.configUrl)
    .then(() => {
      window.dispatchEvent(new Event('load'));
      document.getElementById(scriptId)?.remove();
    })
    .catch((error) => {
      console.error(error);
    });
}

export default sayWhattChat;
